import { Link } from 'react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/useAuth'
import { usePermissions } from '../../hooks/usePermissions'
import { DashboardKpiCard, LinkButton, StatusBadge, type BadgeColor } from '../../components/ui'
import apiClient from '../../api/client'
import type {
  DashboardStatsDto,
  PropertyMonthCountDto,
  PropertyTypeCountDto,
  TimelineEventDto,
  PlannerEventUpcomingDto,
  TodoListItemDto,
  VisitListItemDto,
  RequestWithNewMatchesDto,
} from '../../api/generated/dtos'
import { Line, Doughnut } from 'react-chartjs-2'
import { getDesignColor, getDesignColorWithAlpha } from '../../lib/designTokens'
import { getTimelineEventDescription, getTimelineEventTypeLabel } from '../../lib/timelineLabels'
import { DeadlineList } from '../../components/deadlines/DeadlineList'
import { TimelineEventDetailsModal, type TimelineEventDetails } from '../../components/timeline/TimelineEventDetailsModal'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js'
import {
  Building2,
  Users,
  ClipboardList,
  CalendarCheck,
  Euro,
  MailCheck,
  Mail,
  Target,
  CheckCircle,
  Flame,
  Globe,
  UserPlus,
  Home,
  Check,
  TrendingUp,
  Eye,
  History,
  Inbox,
  CalendarX,
  AlarmClock,
  Clock,
  MapPin,
  BarChart3,
  PieChart,
  RefreshCw,
  ClipboardPlus,
  FileUp,
  Image,
  MessageSquare,
  Circle,
  AlertTriangle,
  Plus,
  ListChecks,
  Sparkles,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement)

type DashboardStats = DashboardStatsDto

type TrendItem = PropertyMonthCountDto

type TypeItem = PropertyTypeCountDto

// Palette dei grafici derivata dai token del design system: l'accento brand
// resta primario, mentre stati e serie secondarie mantengono colori distinti.
const TYPE_COLOR_TOKENS = [
  'primary-500', 'success-500', 'info-500', 'warning-500', 'danger-500',
  'secondary-500', 'primary-600', 'success-700', 'info-700', 'warning-700',
]

// "Created"/"Modified"/"Deleted" sono generici e condivisi da più entità (Property, Customer,
// Request...): serve la combinazione "EntityType:EventType" per distinguerli, altrimenti
// ricadono tutti sulla stessa icona di fallback - bug osservato in produzione, ogni evento di
// creazione mostrava lo stesso cerchio vuoto indipendentemente dal tipo di entità.
const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  'Property:Created': Building2,
  'Customer:Created': UserPlus,
  'Request:Created': ClipboardList,
  'Assignment:Created': ClipboardPlus,
  VisitScheduled: CalendarCheck,
  VisitCompleted: CheckCircle,
  OfferReceived: Euro,
  OfferAccepted: CheckCircle,
  OfferRejected: XCircle,
  EmailSent: Mail,
  StatusChanged: RefreshCw,
  AssignmentCreated: ClipboardPlus,
  DocumentUploaded: FileUp,
  PhotoUploaded: Image,
  NoteAdded: MessageSquare,
}

function getActivityIcon(activity: TimelineEventDto): LucideIcon {
  return ACTIVITY_ICONS[`${activity.entityType}:${activity.eventType}`] || ACTIVITY_ICONS[activity.eventType] || Circle
}

function getActivityUrl(activity: TimelineEventDto): string {
  if (activity.propertyId || activity.entityType === 'Property') return `/properties/${activity.propertyId ?? activity.entityId}`
  if (activity.customerId) return `/customers/${activity.customerId}`
  if (activity.requestId) return `/requests/${activity.requestId}`
  if (activity.assignmentId) return `/assignments/${activity.assignmentId}`
  return '/timeline'
}

function getUpcomingEventUrl(evt: PlannerEventUpcomingDto): string {
  if (evt.sourceType === 'Visit') return `/visits/${evt.eventId}`
  return `/planner/${evt.eventId}`
}

// Priority è una stringa libera lato server (Low/Normal/High/Urgent), stessa convenzione
// usata in TodosPage.tsx.
const PRIORITY_LABELS: Record<string, { label: string; color: BadgeColor }> = {
  Low: { label: 'Bassa', color: 'secondary' },
  Normal: { label: 'Media', color: 'info' },
  High: { label: 'Alta', color: 'warning' },
  Urgent: { label: 'Urgente', color: 'danger' },
}

// Riutilizzato sia per "I miei ToDo" (Agent) sia per "ToDo Agenzia" (Manager/Admin):
// l'endpoint /todos/active è già scoped per ruolo lato backend (TodoApiController.GetTodoUserIdScope),
// quindi il body della lista è identico, cambia solo il titolo del pannello.
function TodoListBody({ todos, loading, error }: { todos?: TodoListItemDto[]; loading: boolean; error: boolean }) {
  const [selectedTodo, setSelectedTodo] = useState<TodoListItemDto | null>(null)
  if (error) {
    return (
      <div className="py-5 text-center text-negative">
        <AlertTriangle size={48} className="mx-auto" />
        <p className="mt-2">Impossibile caricare i ToDo. Riprova più tardi.</p>
      </div>
    )
  }
  if (loading) {
    return <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento ToDo…</div>
  }
  if (!todos || todos.length === 0) {
    return (
      <div className="py-5 text-center text-muted">
        <ListChecks size={48} className="mx-auto" />
        <p className="mt-2">Nessun ToDo attivo</p>
      </div>
    )
  }
  const overdueCount = todos.filter((todo) => todo.isOverdue).length
  const dueSoonCount = todos.filter((todo) => {
    if (!todo.dueDate || todo.isOverdue) return false
    const due = new Date(todo.dueDate)
    const now = new Date()
    const endOfWeek = new Date(now)
    endOfWeek.setDate(now.getDate() + 7)
    return due >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && due <= endOfWeek
  }).length
  return (
    <>
      <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-border-subtle bg-surface px-3 py-2 text-xs text-muted">
        <span><strong className={overdueCount ? 'text-negative' : 'text-heading'}>{overdueCount}</strong> scaduti</span>
        <span><strong className="text-heading">{dueSoonCount}</strong> in scadenza nei prossimi 7 giorni</span>
        <span><strong className="text-heading">{todos.length}</strong> attivi</span>
      </div>
      <div className="divide-y divide-border-subtle">
      {todos.slice(0, 8).map((todo) => {
        const priority = PRIORITY_LABELS[todo.priority] ?? PRIORITY_LABELS.Normal
        return (
          <div className="block p-3 hover:bg-surface" key={todo.todoId}>
            <div className="flex w-full items-start justify-between gap-3">
              <div className="min-w-0">
                <Link to={`/todos/${todo.todoId}`} className="mb-1 block font-semibold text-link hover:underline">{todo.title}</Link>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted">
                  {todo.customerName && (todo.customerId ? <Link to={`/customers/${todo.customerId}`} className="hover:text-link hover:underline">{todo.customerName}</Link> : <span>{todo.customerName}</span>)}
                  {todo.propertyId && <Link to={`/properties/${todo.propertyId}`} className="hover:text-link hover:underline">{todo.propertyCode}{todo.propertyAddress ? ` · ${todo.propertyAddress}` : ''}</Link>}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <StatusBadge color={priority.color} label={priority.label} solid />
                {todo.dueDate && (
                  <small className={todo.isOverdue ? 'font-semibold text-negative' : 'text-muted'}>
                    {new Date(todo.dueDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                  </small>
                )}
              </div>
            </div>
            <button type="button" onClick={() => setSelectedTodo(todo)} className="mt-2 rounded px-2 py-0.5 text-xs font-medium text-link hover:bg-primary-50">Dettagli</button>
          </div>
        )
      })}
      </div>
      <TimelineEventDetailsModal
        event={selectedTodo ? {
          heading: 'Dettaglio ToDo', title: selectedTodo.title, typeLabel: 'ToDo', entityLabel: 'Attività',
          description: selectedTodo.description, createdAt: selectedTodo.dueDate, dateLabel: 'Scadenza',
          priorityLabel: (PRIORITY_LABELS[selectedTodo.priority] ?? PRIORITY_LABELS.Normal).label,
          detailUrl: `/todos/${selectedTodo.todoId}`, detailUrlLabel: 'Apri ToDo',
          propertyId: selectedTodo.propertyId, propertyCode: selectedTodo.propertyCode, propertyAddress: selectedTodo.propertyAddress,
          customerId: selectedTodo.customerId, customerName: selectedTodo.customerName,
        } : null}
        onClose={() => setSelectedTodo(null)}
      />
    </>
  )
}

function getActivityEntityLabel(entityType: string): string {
  const labels: Record<string, string> = {
    Property: 'Immobile', Customer: 'Cliente', Request: 'Richiesta', Assignment: 'Incarico',
    Visit: 'Visita', Offer: 'Proposta', Document: 'Documento', Todo: 'ToDo',
  }
  return labels[entityType] ?? entityType
}

export function DashboardPage() {
  const [selectedActivity, setSelectedActivity] = useState<TimelineEventDetails | null>(null)
  const [selectedUpcomingEvent, setSelectedUpcomingEvent] = useState<PlannerEventUpcomingDto | null>(null)
  const { user } = useAuth()
  const { can } = usePermissions()
  const primaryColor = getDesignColor('primary-500')
  const canvasColor = getDesignColor('canvas')
  const typeColors = TYPE_COLOR_TOKENS.map(getDesignColor)
  // In precedenza confrontava `role === 'Agent'`: un dipendente LimitedUser (permessi
  // "solo propri dati" secondo Permissions.GetPermissionsForRole, stesso tier di Agent)
  // risultava `false` qui e vedeva la vista Manager/Admin - grafici e KPI dell'intera
  // agenzia invece dei soli propri dati, nonostante il ruolo dichiari visibilità ridotta.
  // Il permesso ViewAll è la stessa fonte di verità usata lato backend per lo scoping
  // reale delle query (Permissions.IsAgentTierRole), quindi frontend e backend restano
  // sempre coerenti anche se in futuro cambia il vocabolario dei ruoli.
  const isAgentView = !can('Permissions.Properties.ViewAll')

  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useQuery<DashboardStats>({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      const res = await apiClient.get('/statistics/dashboard')
      return res.data
    },
  })

  // Scoping per ruolo già gestito lato backend (TodoApiController.GetTodoUserIdScope):
  // Agent vede solo i propri ToDo, Manager/Admin quelli di tutta l'agenzia - stesso
  // endpoint per entrambe le viste, cambia solo il titolo del pannello.
  const { data: todos, isLoading: todosLoading, isError: todosError } = useQuery<TodoListItemDto[]>({
    queryKey: ['todos', 'active'],
    queryFn: async () => {
      const res = await apiClient.get('/todos/active')
      return res.data
    },
  })

  // Solo per la vista Agent: le proprie visite in agenda, non caricate per Manager/Admin
  // che vedono già "Andamento Mensile Proprietà" e "Tipologie Immobili" al loro posto.
  const { data: myVisits, isLoading: myVisitsLoading, isError: myVisitsError } = useQuery<VisitListItemDto[]>({
    queryKey: ['visits', 'upcoming'],
    queryFn: async () => {
      const res = await apiClient.get('/visits/upcoming', { params: { days: 7 } })
      return res.data
    },
    enabled: isAgentView,
  })

  // Scoping per ruolo già gestito lato backend (RequestsApiController.GetWithNewMatches):
  // Agent vede solo le proprie richieste, Manager/Admin quelle di tutta l'agenzia.
  const { data: matchingRequests, isLoading: matchingRequestsLoading, isError: matchingRequestsError } = useQuery<RequestWithNewMatchesDto[]>({
    queryKey: ['requests', 'with-new-matches'],
    queryFn: async () => {
      const res = await apiClient.get('/requests/with-new-matches')
      return res.data
    },
  })

  const { data: trendData, isLoading: trendLoading, isError: trendError } = useQuery<{ data: TrendItem[] }>({
    queryKey: ['statistics', 'insertion-trend'],
    queryFn: async () => {
      const res = await apiClient.get('/statistics/properties/insertion-trend', { params: { months: 6 } })
      return res.data
    },
  })

  const { data: typeData, isLoading: typeLoading, isError: typeError } = useQuery<{ data: TypeItem[] }>({
    queryKey: ['statistics', 'by-type'],
    queryFn: async () => {
      const res = await apiClient.get('/statistics/properties/by-type')
      return res.data
    },
  })

  // Attività recenti (timeline)
  const { data: recentActivity, isLoading: recentActivityLoading, isError: recentActivityError } = useQuery<TimelineEventDto[]>({
    queryKey: ['timeline', 'recent'],
    queryFn: async () => {
      const res = await apiClient.get('/timeline/GetEvents', { params: { limit: 8 } })
      return Array.isArray(res.data) ? res.data : []
    },
  })

  // Prossimi eventi (planner)
  const { data: upcomingEvents, isLoading: upcomingEventsLoading, isError: upcomingEventsError } = useQuery<PlannerEventUpcomingDto[]>({
    queryKey: ['planner', 'upcoming'],
    queryFn: async () => {
      const res = await apiClient.get('/planner/upcoming', { params: { days: 7 } })
      return Array.isArray(res.data) ? res.data : []
    },
  })

  // Chart data per andamento mensile
  const trendItems = trendData?.data ?? []
  const lineChartData = {
    labels: trendItems.map((item) => {
      const [year, month] = item.month.split('-')
      const date = new Date(parseInt(year), parseInt(month) - 1)
      return date.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' })
    }),
    datasets: [
      {
        label: 'Nuovi immobili',
        data: trendItems.map((item) => item.count),
        borderColor: primaryColor,
        backgroundColor: getDesignColorWithAlpha('primary-500', 0.1),
        fill: true,
        tension: 0.3,
        pointBackgroundColor: primaryColor,
        pointBorderColor: canvasColor,
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  }

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number } }) => `${ctx.parsed.y} immobili`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  }

  // Chart data per tipologie
  const typeItems = typeData?.data ?? []
  const doughnutChartData = {
    labels: typeItems.map((item) => item.typeName),
    datasets: [
      {
        data: typeItems.map((item) => item.count),
        backgroundColor: typeItems.map((_, i) => typeColors[i % typeColors.length]),
        borderWidth: 2,
        borderColor: canvasColor,
      },
    ],
  }

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { padding: 12, usePointStyle: true, pointStyle: 'circle' },
      },
      // Default Chart.js: boxPadding è 0, il quadratino colore tocca il testo del
      // valore ("Villa" seguito da "☐6" senza spazio). boxPadding aggiunge il gap.
      tooltip: {
        boxPadding: 6,
        padding: 10,
      },
    },
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-5 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="m-0 text-xs font-semibold tracking-[0.12em] text-muted uppercase">
            {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-heading sm:text-3xl">
            Buongiorno, {user?.fullName || user?.email}
          </h1>
          <p className="m-0 mt-1 text-sm text-muted">Ecco cosa succede nella tua agenzia.</p>
        </div>
        {can('Permissions.Properties.Create') && (
          <LinkButton to="/properties/create" icon={<Plus size={16} />}>
            Nuovo immobile
          </LinkButton>
        )}
      </div>

      {statsError ? (
        <div className="mb-5 flex flex-col items-center gap-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-8 text-center text-negative">
          <AlertTriangle size={40} />
          <p className="m-0">Impossibile caricare le statistiche della dashboard. Riprova più tardi.</p>
          <button type="button" onClick={() => refetchStats()} className="rounded-md border border-danger-300 px-3 py-1 text-sm font-medium text-negative hover:bg-danger-100">
            Riprova
          </button>
        </div>
      ) : (
        <>
          {/* Row 1: KPI Cards principali */}
          <div className="dashboard-kpi-container mb-5">
          <div className="dashboard-kpi-grid">
            <DashboardKpiCard title={isAgentView ? 'I Miei Immobili' : 'Immobili Totali'} value={stats?.totalProperties} icon={<Building2 size={36} />} color="primary" to="/properties" loading={statsLoading}
              subMetric={`${stats?.activeProperties ?? 0} attivi`} subMetricColor="success" subMetricIcon={<CheckCircle size={13} />}
              footerText={`${stats?.publishedProperties ?? 0} pubblicati`} footerIcon={<Globe size={13} />} />
            <DashboardKpiCard title={isAgentView ? 'I Miei Clienti' : 'Clienti Totali'} value={stats?.totalCustomers} icon={<Users size={36} />} color="primary" to="/customers" loading={statsLoading}
              subMetric={`${stats?.activeCustomers ?? 0} attivi`} subMetricColor="success" subMetricIcon={<CheckCircle size={13} />}
              footerText={`+${stats?.newCustomersThisMonth ?? 0} questo mese`} footerIcon={<UserPlus size={13} />} />
            <DashboardKpiCard title={isAgentView ? 'Le Mie Richieste' : 'Richieste Attive'} value={stats?.activeRequests} icon={<ClipboardList size={36} />} color="primary" to="/requests" loading={statsLoading}
              subMetric={`${stats?.urgentRequests ?? 0} urgenti`} subMetricColor="danger" subMetricIcon={<Flame size={13} />}
              footerText={`${stats?.requestsWithMatches ?? 0} con matches`} footerIcon={<Home size={13} />} />
            <DashboardKpiCard title="Visite Prossime" value={stats?.upcomingVisits7Days} icon={<CalendarCheck size={36} />} color="primary" to="/planner" loading={statsLoading}
              subMetric="prossimi 7 giorni"
              footerText={`${stats?.visitsThisMonth ?? 0} questo mese`} footerIcon={<Check size={13} />} />
          </div>
          </div>

          {/* Row 2: KPI Cards secondarie */}
          <div className="dashboard-kpi-container mb-5">
          <div className="dashboard-kpi-grid">
            <DashboardKpiCard title={isAgentView ? 'Le Mie Offerte' : 'Offerte Attive'} value={stats?.activeOffers} icon={<Euro size={36} />} color="primary" to="/offers" loading={statsLoading}
              subMetric={`${stats?.offersPendingResponse ?? 0} in attesa di risposta`}
              footerText={`${stats?.offersCountered ?? 0} contro-offerte`} footerIcon={<RefreshCw size={13} />} />
            <DashboardKpiCard title="Proposte Inviate" value={stats?.proposalsSentThisMonth} icon={<MailCheck size={36} />} color="primary" to="/requests" loading={statsLoading}
              subMetric="questo mese"
              footerText={`${stats?.proposalsConversionRate ?? 0}% conversion`} footerIcon={<TrendingUp size={13} />} />
            <DashboardKpiCard title="Email Inviate" value={stats?.emailsSentThisMonth} icon={<Mail size={36} />} color="primary" to="/emails" loading={statsLoading}
              subMetric="questo mese"
              footerText={`${stats?.emailOpenRate ?? 0}% aperture`} footerIcon={<Eye size={13} />} />
            <DashboardKpiCard title="Match Success" value={`${stats?.matchSuccessRate ?? 0}%`} icon={<Target size={36} />} color="success" to="/requests" loading={statsLoading}
              subMetric="richieste matchate" />
          </div>
          </div>
        </>
      )}

      {/* Row 3: per Agent, lavoro personale (ToDo + visite); per Manager/Admin, i grafici agenzia */}
      {isAgentView ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          {/* I miei ToDo */}
          <div className="rounded-lg border border-border bg-canvas xl:col-span-2">
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
              <h5 className="m-0 flex items-center gap-2 text-base font-semibold">
                <ListChecks size={18} />
                I miei ToDo
              </h5>
              <Link to="/todos" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
                Vedi tutti
              </Link>
            </div>
            <TodoListBody todos={todos} loading={todosLoading} error={todosError} />
          </div>

          {/* Le mie visite prossime */}
          <div className="rounded-lg border border-border bg-canvas">
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
              <h5 className="m-0 flex items-center gap-2 text-nowrap text-base font-semibold">
                <CalendarCheck size={18} />
                Le mie visite
              </h5>
              <Link to="/visits" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
                Vedi tutte
              </Link>
            </div>
            <div>
              {myVisitsError ? (
                <div className="py-5 text-center text-negative">
                  <AlertTriangle size={48} className="mx-auto" />
                  <p className="mt-2">Impossibile caricare le visite. Riprova più tardi.</p>
                </div>
              ) : myVisitsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento visite…</div>
              ) : !myVisits || myVisits.length === 0 ? (
                <div className="py-5 text-center text-muted">
                  <CalendarX size={48} className="mx-auto" />
                  <p className="mt-2">Nessuna visita in programma</p>
                </div>
              ) : (
                <div className="divide-y divide-border-subtle">
                  {myVisits.slice(0, 8).map((visit) => (
                    <Link to={`/visits/${visit.visitId}`} className="block p-3 hover:bg-surface" key={visit.visitId}>
                      <div className="min-w-0">
                        <h6 className="mb-1 font-semibold">{visit.propertyTitle || 'Visita'}</h6>
                        <p className="mb-1 flex items-center gap-1 text-sm">
                          <Clock size={13} />
                          {new Date(visit.scheduledDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {visit.customerName && (
                          <small className="flex items-center gap-1 text-muted">
                            <Users size={13} />
                            {visit.customerName}
                          </small>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          {/* Andamento Mensile */}
          <div className="rounded-lg border border-border bg-canvas xl:col-span-2">
            <div className="border-b border-border-subtle px-4 py-3">
              <h6 className="m-0 font-semibold">Andamento Mensile Proprietà</h6>
            </div>
            <div className="p-4">
              <div style={{ height: 300 }}>
                {trendLoading ? (
                  <div className="flex h-full items-center justify-center gap-2 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento andamento…</div>
                ) : trendError ? (
                  <div className="flex h-full items-center justify-center text-center text-sm text-negative">Impossibile caricare l’andamento.</div>
                ) : trendItems.length > 0 ? (
                  <Line
                    data={lineChartData}
                    options={lineChartOptions as Record<string, unknown>}
                    aria-label="Grafico a linee: andamento mensile degli inserimenti immobili"
                    role="img"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted">
                    <div className="text-center">
                      <BarChart3 size={40} className="mx-auto" />
                      <p className="mt-2">Nessun dato disponibile</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tipologie Immobili */}
          <div className="rounded-lg border border-border bg-canvas">
            <div className="border-b border-border-subtle px-4 py-3">
              <h6 className="m-0 font-semibold">Tipologie Immobili</h6>
            </div>
            <div className="p-4">
              <div style={{ height: 300 }}>
                {typeLoading ? (
                  <div className="flex h-full items-center justify-center gap-2 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento tipologie…</div>
                ) : typeError ? (
                  <div className="flex h-full items-center justify-center text-center text-sm text-negative">Impossibile caricare le tipologie.</div>
                ) : typeItems.length > 0 ? (
                  <Doughnut
                    data={doughnutChartData}
                    options={doughnutChartOptions}
                    aria-label="Grafico a ciambella: distribuzione immobili per tipologia"
                    role="img"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted">
                    <div className="text-center">
                      <PieChart size={40} className="mx-auto" />
                      <p className="mt-2">Nessun dato disponibile</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Row 4: Attività Recenti + Prossimi Eventi (+ ToDo Agenzia come terza colonna per Manager/Admin,
          così non resta isolato in una riga a sé con mezza pagina di spazio vuoto sotto) */}
      <div className="mt-3 mb-4 grid grid-cols-1 gap-3 xl:grid-cols-3">
        {/* Attività Recenti */}
        <div className={`rounded-lg border border-border bg-canvas ${isAgentView ? 'xl:col-span-2' : ''}`}>
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <h5 className="m-0 flex items-center gap-2 text-base font-semibold">
              <History size={18} />
              Attività Recenti
            </h5>
            <Link to="/timeline" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
              Vedi tutte
            </Link>
          </div>
          <div>
            {recentActivityError ? (
              <div className="py-5 text-center text-negative">
                <AlertTriangle size={48} className="mx-auto" />
                <p className="mt-2">Impossibile caricare le attività recenti. Riprova più tardi.</p>
              </div>
            ) : recentActivityLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento attività…</div>
            ) : !recentActivity || recentActivity.length === 0 ? (
              <div className="py-5 text-center text-muted">
                <Inbox size={48} className="mx-auto" />
                <p className="mt-2">Nessuna attività recente</p>
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {recentActivity.map((activity, i: number) => {
                  const Icon = getActivityIcon(activity)
                  const url = getActivityUrl(activity)
                  return (
                    <div className="p-3 hover:bg-surface" key={activity.timelineEventId || i}>
                      <div className="flex w-full items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Link to={url} className="mb-1 flex w-fit items-center gap-2 font-semibold text-heading hover:text-link">
                            <Icon size={16} className="text-link" />
                            {activity.title || activity.eventType || 'Evento'}
                          </Link>
                          {activity.description && (
                            <p className="mb-1 text-sm text-muted">{getTimelineEventDescription(activity.description)}</p>
                          )}
                          {(activity.propertyId || activity.entityType === 'Property') && (
                            <p className="mb-1 flex flex-wrap items-center gap-x-2 text-sm font-medium text-heading">
                              <Link to={`/properties/${activity.propertyId ?? activity.entityId}`} className="inline-flex items-center gap-1.5 rounded bg-primary-50 px-1.5 py-0.5 text-xs font-semibold text-link hover:bg-primary-100">
                                {activity.propertyCode || (activity.entityId ? activity.entityId.replaceAll('-', '').slice(0, 8).toUpperCase() : '')}
                                {activity.propertyAddress && <span className="font-medium text-body">· {activity.propertyAddress}</span>}
                              </Link>
                            </p>
                          )}
                          {activity.customerName && (
                            <p className="mb-1 text-sm font-medium text-heading">
                              {activity.customerId
                                ? <Link to={`/customers/${activity.customerId}`} className="text-link hover:underline">{activity.customerName}</Link>
                                : activity.customerName}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
                            <span className="rounded-full bg-surface px-2 py-0.5">{getActivityEntityLabel(activity.entityType)}</span>
                            {activity.user?.fullName && (
                              activity.user.employeeId
                                ? <Link to={`/employees/${activity.user.employeeId}`} className="hover:text-link">Da {activity.user.fullName}</Link>
                                : <span>Da {activity.user.fullName}</span>
                            )}
                            <button
                              type="button"
                              onClick={() => setSelectedActivity({
                                title: activity.title || activity.eventType || 'Evento',
                                typeLabel: getTimelineEventTypeLabel(activity.eventType),
                                entityLabel: getActivityEntityLabel(activity.entityType),
                                description: getTimelineEventDescription(activity.description),
                                createdAt: activity.createdAt,
                                actorName: activity.user?.fullName,
                                actorUrl: activity.user?.employeeId ? `/employees/${activity.user.employeeId}` : undefined,
                                propertyId: activity.propertyId ?? (activity.entityType === 'Property' ? activity.entityId : undefined),
                                propertyCode: activity.propertyCode || (activity.entityType === 'Property' ? activity.entityId.replaceAll('-', '').slice(0, 8).toUpperCase() : undefined),
                                propertyAddress: activity.propertyAddress,
                                customerId: activity.customerId,
                                customerName: activity.customerName,
                              })}
                              className="rounded px-2 py-0.5 font-medium text-link hover:bg-primary-50"
                            >
                              Dettagli
                            </button>
                          </div>
                        </div>
                        <small className="shrink-0 text-nowrap text-muted">
                          {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </small>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Prossimi Eventi */}
        <div className="rounded-lg border border-border bg-canvas">
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <h5 className="m-0 flex items-center gap-2 text-nowrap text-base font-semibold">
              <CalendarCheck size={18} />
              Prossimi Eventi
            </h5>
            <Link to="/planner" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
              Agenda
            </Link>
          </div>
          <div>
            {upcomingEventsError ? (
              <div className="py-5 text-center text-negative">
                <AlertTriangle size={48} className="mx-auto" />
                <p className="mt-2">Impossibile caricare gli eventi. Riprova più tardi.</p>
              </div>
            ) : upcomingEventsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento eventi…</div>
            ) : !upcomingEvents || upcomingEvents.length === 0 ? (
              <div className="py-5 text-center text-muted">
                <CalendarX size={48} className="mx-auto" />
                <p className="mt-2">Nessun evento programmato</p>
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {upcomingEvents.slice(0, 8).map((evt, i: number) => (
                  <div className="block p-3 hover:bg-surface" key={evt.eventId || i}>
                    <div className="min-w-0">
                      <Link to={getUpcomingEventUrl(evt)} className="mb-1 block font-semibold text-link hover:underline">{evt.title || 'Evento'}</Link>
                      <p className="mb-1 flex items-center gap-1 text-sm">
                        <Clock size={13} />
                        {evt.startDate ? new Date(evt.startDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                      {evt.location && (
                        <small className="flex items-center gap-1 text-muted">
                          <MapPin size={13} />
                          {evt.location}
                        </small>
                      )}
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
                        {evt.customerId && evt.customerName && <Link to={`/customers/${evt.customerId}`} className="hover:text-link hover:underline">{evt.customerName}</Link>}
                        {evt.propertyId && <Link to={`/properties/${evt.propertyId}`} className="hover:text-link hover:underline">{evt.propertyCode}{evt.propertyAddress ? ` · ${evt.propertyAddress}` : ''}</Link>}
                      </div>
                      <button type="button" onClick={() => setSelectedUpcomingEvent(evt)} className="mt-2 rounded px-2 py-0.5 text-xs font-medium text-link hover:bg-primary-50">Dettagli</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scadenzario unificato, con lo stesso scope mine/agenzia del backend. */}
        <div className="rounded-lg border border-border bg-canvas shadow-sm">
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <h5 className="m-0 flex items-center gap-2 text-nowrap text-base font-semibold"><AlarmClock size={18} />Prossime scadenze</h5>
            <Link to="/deadlines" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">Scadenzario</Link>
          </div>
          <DeadlineList scope={isAgentView ? 'mine' : 'agency'} compact />
        </div>

        {/* ToDo Agenzia - solo Manager/Admin, che non hanno già "I miei ToDo" in Row 3 */}
        {!isAgentView && (
          <div className="rounded-lg border border-border bg-canvas shadow-sm">
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
              <h5 className="m-0 flex items-center gap-2 text-nowrap text-base font-semibold">
                <ListChecks size={18} />
                ToDo Agenzia
              </h5>
              <Link to="/todos" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
                Vedi tutti
              </Link>
            </div>
            <TodoListBody todos={todos} loading={todosLoading} error={todosError} />
          </div>
        )}
      </div>

      {/* Row 6: Richieste da matchare - visibile a tutti i ruoli, scoping per ruolo già gestito dal backend */}
      <div className="mt-3 mb-4 grid grid-cols-1 gap-3">
        <div className="rounded-lg border border-border bg-canvas shadow-sm">
          <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
            <h5 className="m-0 flex items-center gap-2 text-base font-semibold">
              <Sparkles size={18} />
              Richieste da matchare
            </h5>
            <Link to="/requests" className="rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-link hover:bg-primary-50">
              Vedi tutte
            </Link>
          </div>
          <div>
            {matchingRequestsError ? (
              <div className="py-5 text-center text-negative">
                <AlertTriangle size={48} className="mx-auto" />
                <p className="mt-2">Impossibile caricare le richieste. Riprova più tardi.</p>
              </div>
            ) : matchingRequestsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted" role="status"><span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary-600" />Caricamento richieste…</div>
            ) : !matchingRequests || matchingRequests.length === 0 ? (
              <div className="py-5 text-center text-muted">
                <Target size={48} className="mx-auto" />
                <p className="mt-2">Nessuna richiesta con nuovi match al momento</p>
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {matchingRequests.slice(0, 8).map((r) => (
                  <Link to={`/requests/${r.requestId}`} className="flex items-center justify-between gap-3 p-3 hover:bg-surface" key={r.requestId}>
                    <div className="min-w-0">
                      <h6 className="mb-1 font-semibold">{r.customerName || 'Cliente'}</h6>
                      {(r.minBudget || r.maxBudget) && (
                        <p className="mb-0 text-sm text-muted">
                          {r.minBudget?.toLocaleString('it-IT') ?? '0'} - {r.maxBudget?.toLocaleString('it-IT') ?? '?'} EUR
                        </p>
                      )}
                    </div>
                    <StatusBadge color="success" label={`${r.newMatchesCount} nuovi match`} solid />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <TimelineEventDetailsModal event={selectedActivity} onClose={() => setSelectedActivity(null)} />
      <TimelineEventDetailsModal
        event={selectedUpcomingEvent ? {
          heading: 'Dettaglio evento', title: selectedUpcomingEvent.title, typeLabel: selectedUpcomingEvent.eventType,
          entityLabel: selectedUpcomingEvent.sourceType === 'Visit' ? 'Visita' : 'Evento agenda',
          description: selectedUpcomingEvent.description, createdAt: selectedUpcomingEvent.startDate, dateLabel: 'Inizio',
          location: selectedUpcomingEvent.location, detailUrl: getUpcomingEventUrl(selectedUpcomingEvent), detailUrlLabel: 'Apri evento',
          propertyId: selectedUpcomingEvent.propertyId, propertyCode: selectedUpcomingEvent.propertyCode, propertyAddress: selectedUpcomingEvent.propertyAddress,
          customerId: selectedUpcomingEvent.customerId, customerName: selectedUpcomingEvent.customerName,
        } : null}
        onClose={() => setSelectedUpcomingEvent(null)}
      />
    </div>
  )
}
